import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import SchemaOrg from '../../components/base/SchemaOrg';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const pageTitle = '關於永業環保｜20年環保清潔專業經驗';
    const pageDesc = '永業環保成立於2004年，深耕環保清潔產業20年，累積5000+成功案例。提供水塔清洗、水管疏通、水溝清理、內視鏡檢查等專業服務，政府認證合格，服務桃竹苗地區。';
    const pageKeywords = '永業環保,環保清潔公司,水塔清洗,管道疏通,桃園清潔服務';
    const pageUrl = `${siteUrl}/about`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: '永業環保',
      description: '成立於2004年，深耕環保清潔產業20年，累積超過5000個成功案例。提供水塔清洗、水管疏通、水溝清理、內視鏡檢查等專業服務。',
      url: siteUrl,
      telephone: '0800-281-281',
      foundingDate: '2004',
      address: {
        '@type': 'PostalAddress',
        addressRegion: '桃園市',
        addressCountry: 'TW'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#aboutpage`,
      url: `${siteUrl}/about`,
      name: '關於永業環保',
      description: '了解永業環保的發展歷程、服務理念、專業團隊與企業文化',
      mainEntity: {
        '@id': `${siteUrl}/#organization`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/about#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '關於我們', item: `${siteUrl}/about` }
      ]
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      {/* Hero 區域 */}
      <section className="relative pt-28 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">關於永業環保</h1>
            <p className="text-lg md:text-xl text-gray-600">20年專業經驗 守護您的用水安全</p>
          </div>
        </div>
      </section>

      {/* 公司歷史 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="inline-block bg-[#1e3a5f] text-white px-4 py-2 rounded-full text-sm font-medium mb-6">OUR STORY</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">深耕環保清潔產業<br/>二十載的堅持</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
                <p>永業環保成立於2004年，從一間小型清潔公司起步，憑藉著對品質的堅持與專業的服務態度，逐步成長為桃園地區最受信賴的環保清潔專家。</p>
                <p>我們深知用水安全對每個家庭與企業的重要性，因此不斷引進最新的清潔技術與設備，包括高壓水刀疏通系統、專業內視鏡檢測儀器等，確保為客戶提供最優質的服務。</p>
                <p>二十年來，我們服務超過5000個家庭與企業客戶，累積了豐富的實務經驗。從住宅水塔清洗到大型社區管道維護，從餐廳油脂管線疏通到工廠排水系統清理，每一個案例都是我們專業能力的見證。</p>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-64 md:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/8317f6d62f93446042f4cf364e08a596.jpg" 
                  alt="永業環保團隊"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 服務流程 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block bg-[#1e3a5f] text-white px-6 py-2 rounded-full text-sm font-medium mb-4">SERVICE PROCESS</span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">服務流程</h2>
            <p className="text-gray-600 text-base md:text-lg">簡單五步驟 輕鬆完成清潔服務</p>
          </div>

          <div className="relative">
            {/* 連接線 - 只在桌面版顯示 */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gray-200" style={{ width: 'calc(100% - 200px)', left: '100px' }}></div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 relative">
              {[
                {
                  step: '01',
                  title: '聯絡諮詢',
                  desc: '電話或線上預約，說明您的需求',
                  icon: 'ri-phone-line'
                },
                {
                  step: '02',
                  title: '現場評估',
                  desc: '專業人員到府勘查，提供報價',
                  icon: 'ri-search-line'
                },
                {
                  step: '03',
                  title: '確認施工',
                  desc: '確認時間與費用，安排施工',
                  icon: 'ri-calendar-check-line'
                },
                {
                  step: '04',
                  title: '專業清潔',
                  desc: '使用專業設備進行清潔作業',
                  icon: 'ri-tools-line'
                },
                {
                  step: '05',
                  title: '驗收完成',
                  desc: '客戶驗收滿意後完成付款',
                  icon: 'ri-checkbox-circle-line'
                }
              ].map((process, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                      <i className={`${process.icon} text-2xl md:text-3xl text-white`}></i>
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-gray-200 mb-2">{process.step}</div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{process.title}</h3>
                    <p className="text-sm text-gray-600">{process.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA 區域 */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">準備好體驗專業服務了嗎？</h2>
          <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8">立即聯絡我們，讓永業環保為您解決所有清潔問題</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:0800-281-281" className="w-full sm:w-auto bg-white text-[#1e3a5f] px-8 py-4 rounded-full text-base md:text-lg font-medium hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i>
              立即撥打 0800-281-281
            </a>
            <a href="https://line.me/ti/p/wJ4U8JR3pC" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-base md:text-lg font-medium hover:bg-white hover:text-[#1e3a5f] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-line-fill"></i>
              線上預約
            </a>
          </div>
        </div>
      </section>
    </>
  );
}